"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TabLayout = void 0;
const react_1 = __importDefault(require("react"));
const styled_components_1 = __importDefault(require("styled-components"));
const GlobalStateProvider_1 = require("../../../GlobalStateProvider");
const TabLayoutStyled = styled_components_1.default.div `
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};

	.tab-layout-header {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
	}

	.tab-layout-list {
		display: inline-flex;
		align-items: center;
		background: var(--color-silver, #f3f4f8);
		border-radius: 8px;
		padding: 4px;
		gap: 2px;
		max-width: 100%;
		overflow-x: auto;
	}

	.tab-layout-btn {
		border: none;
		background: transparent;
		padding: ${({ $isMobile }) => ($isMobile ? '8px 10px' : '8px 16px')};
		border-radius: 6px;
		font-family: var(--b4-font-family, 'Noto Sans KR', sans-serif);
		font-size: ${({ $isMobile }) => ($isMobile ? '13px' : 'var(--b4-font-size, 14px)')};
		line-height: ${({ $isMobile }) => ($isMobile ? '20px' : 'var(--b4-line-height, 22px)')};
		letter-spacing: var(--b4-letter-spacing, -0.02em);
		font-weight: var(--b3-font-weight, 400);
		color: var(--color-grey, #666666);
		cursor: pointer;
	}

	.tab-layout-btn.active {
		background: #fff;
		color: var(--color-black, #101010);
		font-weight: 700;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
	}

	.tab-layout-list.equal-btn .tab-layout-btn {
		width: var(--tab-btn-width, auto);
		min-width: var(--tab-btn-min-width, var(--tab-btn-width, auto));
		justify-content: center;
		display: inline-flex;
	}

	${({ $isMobile }) => $isMobile
    ? `
		.tab-layout-list.equal-btn {
			width: 100%;
			display: flex;
		}

		.tab-layout-list.equal-btn .tab-layout-btn {
			flex: 1 1 0;
			width: auto;
		}
	`
    : ''}

	.tab-layout-btn:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	.tab-layout-content {
		width: 100%;
	}

	.tab-layout-pane.hidden {
		display: none;
	}

`;
exports.TabLayout = react_1.default.memo(({ tabs, defaultTabId, activeTabId, onTabChange, className, tabListClassName, contentClassName, keepMounted = false, equalButtonWidth = false, equalButtonMinWidth, buttonSize = 'md', headerRight, }) => {
    const { isMobile } = (0, GlobalStateProvider_1.useGlobalState)();
    const firstTabId = tabs[0]?.id;
    const [internalTabId, setInternalTabId] = react_1.default.useState(defaultTabId || firstTabId);
    react_1.default.useEffect(() => {
        if (!defaultTabId)
            return;
        setInternalTabId(defaultTabId);
    }, [defaultTabId]);
    react_1.default.useEffect(() => {
        if (!tabs.some(tab => tab.id === internalTabId) && firstTabId) {
            setInternalTabId(firstTabId);
        }
    }, [tabs, internalTabId, firstTabId]);
    const currentTabId = activeTabId ?? internalTabId;
    const changeTab = (tabId) => {
        if (activeTabId === undefined) {
            setInternalTabId(tabId);
        }
        onTabChange?.(tabId);
    };
    const activeTab = tabs.find(tab => tab.id === currentTabId) || tabs[0];
    const resolvedButtonWidth = typeof buttonSize === 'number'
        ? `${buttonSize}px`
        : buttonSize === 'sm'
            ? '84px'
            : buttonSize === 'lg'
                ? '128px'
                : buttonSize === 'md'
                    ? '108px'
                    : buttonSize;
    const resolvedButtonMinWidth = typeof equalButtonMinWidth === 'number'
        ? `${equalButtonMinWidth}px`
        : equalButtonMinWidth;
    return (react_1.default.createElement(TabLayoutStyled, { className: className, "$isMobile": isMobile },
        react_1.default.createElement("div", { className: "tab-layout-header" },
            react_1.default.createElement("div", { className: `tab-layout-list ${equalButtonWidth ? 'equal-btn' : ''} ${tabListClassName || ''}`.trim(), role: "tablist", style: equalButtonWidth
                    ? {
                        ['--tab-btn-width']: resolvedButtonWidth,
                        ['--tab-btn-min-width']: resolvedButtonMinWidth || resolvedButtonWidth,
                    }
                    : undefined }, tabs.map(tab => (react_1.default.createElement("button", { key: tab.id, type: "button", className: `tab-layout-btn ${currentTabId === tab.id ? 'active' : ''}`.trim(), onClick: () => changeTab(tab.id), disabled: tab.disabled, role: "tab", "aria-selected": currentTabId === tab.id }, tab.title)))),
            headerRight),
        react_1.default.createElement("div", { className: `tab-layout-content ${contentClassName || ''}`.trim() }, keepMounted
            ? tabs.map(tab => (react_1.default.createElement("div", { key: tab.id, className: `tab-layout-pane ${currentTabId === tab.id ? '' : 'hidden'}`.trim() }, tab.content)))
            : activeTab?.content)));
});
